import { useEffect, useRef } from "react";
import { GoogleMap, MarkerF, CircleF, useJsApiLoader } from "@react-google-maps/api";
import { GOOGLE_MAPS_API_KEY, GOOGLE_MAPS_LIBRARIES, GOOGLE_MAPS_SCRIPT_ID } from "@/lib/googleMaps";

// Centro geográfico aproximado de México, usado cuando aún no hay punto elegido.
const MX_CENTER = { lat: 23.6345, lng: -102.5528 };
const DEFAULT_ZOOM = 4;
const POINT_ZOOM = 16;

const MAP_OPTIONS = { scrollwheel: false, streetViewControl: false, mapTypeControl: false, fullscreenControl: false };

/**
 * Selector de punto para la geocerca de un centro de trabajo.
 * - Clic en el mapa => fija/mueve el punto (llama onChange({lat, lng})).
 * - Dibuja el círculo del radio (metros) alrededor del punto.
 * - Se recentra solo cuando lat/lng cambian desde afuera (p.ej. tras geocodificar el domicilio).
 */
export default function GeoPicker({ lat, lng, radio = 200, onChange, focusSignal = 0, height = 240 }) {
  const { isLoaded, loadError } = useJsApiLoader({
    id: GOOGLE_MAPS_SCRIPT_ID,
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });
  const mapRef = useRef(null);
  const hasPoint = lat != null && lng != null;

  // Solo recentra/hace zoom cuando `focusSignal` cambia (p.ej. tras geocodificar el
  // domicilio o abrir el diálogo). Un clic del usuario en el mapa NO lo dispara, para
  // no dar saltos de zoom mientras se ajusta el punto a mano.
  useEffect(() => {
    if (focusSignal && hasPoint && mapRef.current) {
      mapRef.current.panTo({ lat, lng });
      mapRef.current.setZoom(Math.max(mapRef.current.getZoom(), POINT_ZOOM));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusSignal]);

  if (!GOOGLE_MAPS_API_KEY) {
    return (
      <div
        className="flex items-center justify-center text-sm text-slate-500 bg-slate-50 border border-dashed border-slate-300 rounded-md"
        style={{ height }}
      >
        El mapa no está disponible: falta configurar la API de Google Maps.
      </div>
    );
  }
  if (loadError) {
    return (
      <div
        className="flex items-center justify-center text-sm text-red-600 bg-red-50 border border-dashed border-red-300 rounded-md"
        style={{ height }}
      >
        No se pudo cargar Google Maps. Revisa tu conexión e intenta de nuevo.
      </div>
    );
  }
  if (!isLoaded) {
    return (
      <div
        className="flex items-center justify-center text-sm text-slate-400 bg-slate-50 border border-slate-200 rounded-md"
        style={{ height }}
      >
        Cargando mapa…
      </div>
    );
  }

  return (
    <div className="rounded-md overflow-hidden border border-slate-200">
      <GoogleMap
        mapContainerStyle={{ height, width: "100%" }}
        center={hasPoint ? { lat, lng } : MX_CENTER}
        zoom={hasPoint ? POINT_ZOOM : DEFAULT_ZOOM}
        options={MAP_OPTIONS}
        onLoad={(map) => { mapRef.current = map; }}
        onClick={(e) => onChange({ lat: Number(e.latLng.lat().toFixed(6)), lng: Number(e.latLng.lng().toFixed(6)) })}
      >
        {hasPoint && (
          <>
            <CircleF
              center={{ lat, lng }}
              radius={radio || 0}
              options={{ strokeColor: "#0f172a", strokeWeight: 1, fillColor: "#0f172a", fillOpacity: 0.08, clickable: false }}
            />
            <MarkerF
              position={{ lat, lng }}
              icon={{
                path: window.google.maps.SymbolPath.CIRCLE,
                scale: 7,
                fillColor: "#0f172a",
                fillOpacity: 1,
                strokeColor: "#ffffff",
                strokeWeight: 2,
              }}
            />
          </>
        )}
      </GoogleMap>
    </div>
  );
}
